import React from "react";
import { graphql, PageProps } from "gatsby";
import { SuiteCategoryQuery } from "types/graphql-types";

import OpenGraph from "components/SEO/OpenGraph";
import { ResponsiveSlideshow } from "components/Slideshow/Slideshow";
import {
  DescriptionTextColumnContent,
  DescriptionTextColumnContentLeft,
  DescriptionTextColumnContentRight,
  DescriptionTextColumnLeadIn,
  DescriptionTextColumnTitle,
  SectionContainer
} from "@silversea-ssc/ui-components-react";
import { DescriptionTextColumn } from "@silversea-ssc/ui-components-react";
import TextBox from "components/TextBox/TextBox";
import { MosaicLayout } from "@silversea-ssc/ui-components-react";
import Slider, { SliderSlide } from "components/Slider";
import SanityBuilderToComponents, { hasContent } from "components/PageBuilder";
import PageBuilderSectionTitle from "components/PageBuilder/PageBuilderSectionTitle";
import { arrayIsNotEmpty, isPresent } from "src/utils/checks";
import LinkPictureCard from "components/Card/LinkPictureCard";
import LinkCard from "components/Card/LinkCard";

const SuiteCategoryTemplate: React.FC<PageProps<SuiteCategoryQuery>> = (
  props,
  location
) => {
  const { suite, categories, destinationsSection, page } = props.data;
  const description = suite?.description;
  const name = description?.title?.localized;
  const suiteImages = suite?.images?.filter(isPresent) || [];

  const isAvailableOnHeadingWithName = {
    ...page?.isAvailableOnHeading,
    title: {
      localized: `${name} ${page?.isAvailableOnHeading?.title?.localized}`
    }
  };

  return (
    <React.Fragment>
      <OpenGraph
        openGraph={suite?.metadata?.openGraph}
        excludeFromSitemap={suite?.metadata?.excludeFromSitemap}
      />

      <SectionContainer spacingBottom="regular" spacingTop="regular">
        <DescriptionTextColumn>
          {description?.title && (
            <DescriptionTextColumnTitle>
              {description.title.localized}
            </DescriptionTextColumnTitle>
          )}
          {description?.subtitle && (
            <DescriptionTextColumnLeadIn>
              {description.subtitle.localized}
            </DescriptionTextColumnLeadIn>
          )}

          <DescriptionTextColumnContent>
            <DescriptionTextColumnContentLeft>
              <TextBox
                fontSize="medium"
                content={description?.content?.localized}
              />
            </DescriptionTextColumnContentLeft>

            <DescriptionTextColumnContentRight>
              {arrayIsNotEmpty(suiteImages) && (
                <ResponsiveSlideshow items={suiteImages} variant="gray" />
              )}
            </DescriptionTextColumnContentRight>
          </DescriptionTextColumnContent>
        </DescriptionTextColumn>
      </SectionContainer>
      <SectionContainer
        backgroundColor="neutral05"
        spacingBottom="regular"
        spacingTop="regular"
      >
        <PageBuilderSectionTitle {...isAvailableOnHeadingWithName} />
        <MosaicLayout variant="column3" data-content-name="ships_mosaic">
          {suite?.ships?.map((item) => (
            <LinkPictureCard
              borderColor="transparent"
              key={item?._id}
              url={item?.fullPath}
              title={item?.card?.title?.localized || ""}
              image={item?.card?.image}
              trackingName={item?.metadata?.tracking?.name}
            />
          ))}
        </MosaicLayout>
      </SectionContainer>

      <SectionContainer spacingBottom="regular" spacingTop="regular">
        {!!page?.otherSuitesHeading && (
          <PageBuilderSectionTitle {...page.otherSuitesHeading} />
        )}
        <Slider
          variantColor="neutral05"
          data-content-name="suite_categories_slider"
        >
          {categories.nodes.map((item) => (
            <SliderSlide key={item._id}>
              <LinkCard
                backgroundColor="neutral05"
                key={item._id}
                title={item.card?.title?.localized || ""}
                image={item?.card?.image}
                description={item.card?.description?.localized}
                url={item.fullPath}
              />
            </SliderSlide>
          ))}
        </Slider>
      </SectionContainer>
      {destinationsSection && hasContent(destinationsSection.sections) && (
        <SanityBuilderToComponents
          sections={destinationsSection.sections}
          location={location}
          initialBackgroundColor="neutral05"
        />
      )}
    </React.Fragment>
  );
};

export const query = graphql`
  query SuiteCategory($id: String!) {
    page: sanitySuiteCategoryTemplate {
      isAvailableOnHeading {
        ...SectionTitle
        ...SectionTitleCTAs
      }

      otherSuitesHeading {
        ...SectionTitle
        ...SectionTitleCTAs
      }
    }

    suite: sanitySuiteCategory(id: { eq: $id }) {
      metadata {
        excludeFromSitemap
        openGraph {
          ...OpenGraphMetaTags
        }
      }

      images {
        ...SlideshowOnlyImage
      }

      name {
        localized
      }

      description {
        ...PageBuilderOpeningText
      }

      ships {
        _id
        fullPath
        metadata {
          tracking {
            name
          }
        }
        card {
          ...AsCard
        }
      }
    }

    categories: allSanitySuiteCategory(
      sort: { order: ASC, fields: [order, card___title___en] }
      filter: { id: { ne: $id } }
    ) {
      nodes {
        _id
        metadata {
          tracking {
            name
          }
        }
        fullPath
        card {
          ...AsCard
        }
      }
    }

    destinationsSection: sanitySharedSection(
      slug: { current: { eq: "destinations-slider" } }
    ) {
      sections {
        ...PageBuilderSections
      }
    }
  }
`;

export default React.memo(SuiteCategoryTemplate);
